import { shareImageOrText } from "@/utils/share";
import type { RankingRow } from "@/modules/leagues/types/ranking";

const WIDTH = 720;
const ROW_HEIGHT = 64;
const HEADER_HEIGHT = 130;
const FOOTER_HEIGHT = 50;
const PADDING = 24;
const MAX_ROWS = 10; // top 10 na imagem — ranking completo continua na tela, isto é só o compartilhável

/**
 * Desenha o ranking (Geral/Rodada/Mês) num canvas e gera um PNG pra
 * compartilhar — canvas nativo do navegador, sem biblioteca nova (mesma
 * decisão já tomada no recorte de foto de perfil, por não conseguir
 * instalar/testar pacote novo neste ambiente). Cores fixas (não segue o
 * tema claro/escuro do app) de propósito — a imagem vai pra fora do app
 * (WhatsApp etc.), então precisa ser legível sozinha, sem depender do
 * tema de quem está compartilhando.
 */
async function drawRankingImage(rows: RankingRow[], title: string, leagueName: string): Promise<Blob | null> {
  const visibleRows = rows.slice(0, MAX_ROWS);
  const height = HEADER_HEIGHT + visibleRows.length * ROW_HEIGHT + FOOTER_HEIGHT;

  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // Fundo
  const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
  bgGradient.addColorStop(0, "#0D47A1");
  bgGradient.addColorStop(0.18, "#0B1220");
  bgGradient.addColorStop(1, "#0B1220");
  ctx.fillStyle = bgGradient;
  ctx.fillRect(0, 0, WIDTH, height);

  // Cabeçalho
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "bold 30px Arial, sans-serif";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("🏆 Palpiteiro", PADDING, 48);
  ctx.font = "600 20px Arial, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.fillText(leagueName, PADDING, 76);
  ctx.font = "700 16px Arial, sans-serif";
  ctx.fillStyle = "#FFC107";
  ctx.fillText(title.toUpperCase(), PADDING, 102);

  // Linhas do ranking
  visibleRows.forEach((row, idx) => {
    const y = HEADER_HEIGHT + idx * ROW_HEIGHT;
    if (idx % 2 === 0) {
      ctx.fillStyle = "rgba(255,255,255,0.04)";
      ctx.fillRect(0, y, WIDTH, ROW_HEIGHT);
    }

    // Posição
    const medalColors: Record<number, string> = { 1: "#FFC107", 2: "#C0C0C0", 3: "#CD7F32" };
    ctx.fillStyle = medalColors[row.posicao] ?? "rgba(255,255,255,0.5)";
    ctx.font = "bold 22px Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(`${row.posicao}°`, PADDING + 20, y + ROW_HEIGHT / 2 + 8);
    ctx.textAlign = "left";

    // Avatar (círculo com iniciais — nunca carrega imagem externa aqui,
    // pra não depender de CORS de um domínio de foto de perfil qualquer)
    const initials = row.nome
      .split(" ")
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase();
    const cx = PADDING + 70;
    const cy = y + ROW_HEIGHT / 2;
    ctx.beginPath();
    ctx.arc(cx, cy, 18, 0, Math.PI * 2);
    ctx.fillStyle = "#1976D2";
    ctx.fill();
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "bold 14px Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(initials, cx, cy + 5);
    ctx.textAlign = "left";

    // Nome
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "600 18px Arial, sans-serif";
    const maxNameWidth = WIDTH - PADDING - 200 - 100;
    let displayName = row.nome;
    while (ctx.measureText(displayName).width > maxNameWidth && displayName.length > 3) {
      displayName = displayName.slice(0, -1);
    }
    if (displayName !== row.nome) displayName += "…";
    ctx.fillText(displayName, PADDING + 100, y + ROW_HEIGHT / 2 + 6);

    // Pontos
    ctx.fillStyle = "#FFC107";
    ctx.font = "bold 22px Arial, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(`${row.pontos} pts`, WIDTH - PADDING, y + ROW_HEIGHT / 2 + 8);
    ctx.textAlign = "left";
  });

  // Rodapé
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = "13px Arial, sans-serif";
  ctx.fillText("Crie seu bolão em palpiteirocerto.com.br", PADDING, height - 20);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png");
  });
}

/** Texto que acompanha o compartilhamento — pedido explícito do usuário:
 * só a chamada, sem repetir a pontuação (que já vai na imagem). Usado
 * tanto quando a imagem sai certo quanto no fallback só-texto (se a
 * imagem falhar por algum motivo) — nesse segundo caso, mesmo sem
 * imagem, mantemos só a chamada mesmo assim (simplicidade pedida vale
 * pros dois casos, e o ranking completo continua sempre na tela do app). */
function buildShareText(): string {
  return "Palpiteiro, confira como está a classificação!";
}

export async function shareRanking(rows: RankingRow[], title: string, leagueName: string): Promise<"shared" | "copied" | "failed"> {
  if (rows.length === 0) return "failed";
  let imageBlob: Blob | null = null;
  try {
    imageBlob = await drawRankingImage(rows, title, leagueName);
  } catch {
    imageBlob = null; // segue só com texto, nunca quebra o compartilhamento por causa da imagem
  }
  return shareImageOrText({
    title: "Palpiteiro",
    text: buildShareText(),
    fileName: "ranking-palpiteiro.png",
    imageBlob,
  });
}
